import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  HelpCircle,
  X,
  ArrowRight,
  ArrowLeft,
  Upload,
  Eye,
  Layout,
  ScrollText as Receipt,
  Brain,
  BarChart3,
  Landmark,
  BookOpen,
  Coins,
} from 'lucide-react';

interface Step {
  title: string;
  body: string;
  icon: React.ReactNode;
  cta?: { label: string; route: string };
}

const STEPS: Step[] = [
  {
    title: 'Welcome to ViewPoint',
    body: 'A quick tour, in the order of the menu. You can close it anytime — it stays available from the help button in the bottom right.',
    icon: <BookOpen className="w-5 h-5" />,
  },
  {
    title: '1. Dashboard',
    body: 'Your home screen. See document counts by status, monthly activity, money in and out, and what is overdue.',
    icon: <BarChart3 className="w-5 h-5" />,
    cta: { label: 'Open Dashboard', route: '/dashboard' },
  },
  {
    title: '2. Uploaded Invoices',
    body: 'Drop a PDF and ViewPoint auto-extracts the key fields. Open any invoice to read it in plain language on the Details tab, fix anything that\'s off, or hit Ask AI — every correction teaches the system.',
    icon: <Upload className="w-5 h-5" />,
    cta: { label: 'Uploaded Invoices', route: '/uploaded-invoices' },
  },
  {
    title: '3. Templates',
    body: 'Get regular bills from the same supplier? Upload one of their invoices as a template and ViewPoint reads their future invoices more accurately — no setup. Your plan includes a set number of templates.',
    icon: <Layout className="w-5 h-5" />,
    cta: { label: 'Open Templates', route: '/uploaded-invoices/templates' },
  },
  {
    title: '4. Created Invoices',
    body: 'Generate professional invoices from scratch — Standard, VAT, Proforma, or Credit Notes — and track them from Ready to Process → Confirmed → Sent → Paid.',
    icon: <Receipt className="w-5 h-5" />,
    cta: { label: 'Created Invoices', route: '/created-invoices' },
  },
  {
    title: '5. Expenses',
    body: 'Track money going out. Log expenses, see what you\'ve spent by category, and keep a clear picture of your outgoings alongside your invoices. You can also just describe an expense to the AI Assistant and confirm the card it fills in.',
    icon: <Coins className="w-5 h-5" />,
    cta: { label: 'Open Expenses', route: '/expenses' },
  },
  {
    title: '6. AI Assistant',
    body: 'Ask anything in plain English: "How much did I spend on software in February?" or "Show overdue invoices over €1,000". Say "record an expense of 240 EUR for software" and it fills in a card for you to check and save. You can also hit Ask AI from any invoice or field to jump straight in with context.',
    icon: <Brain className="w-5 h-5" />,
    cta: { label: 'Open AI Assistant', route: '/ai' },
  },
  {
    title: 'KSeF e-invoicing (Advanced)',
    body: 'On the Advanced plan, submit Polish invoices directly to the Ministry of Finance (KSeF). Test, Demo, and Production environments supported.',
    icon: <Landmark className="w-5 h-5" />,
    cta: { label: 'See Plans', route: '/pricing' },
  },
  {
    title: 'Full Guide',
    body: 'That\'s the tour. The Guide page has detailed walkthroughs, examples, and tips for every feature.',
    icon: <Eye className="w-5 h-5" />,
    cta: { label: 'Open Full Guide', route: '/guide' },
  },
];

const STORAGE_KEY = 'helpWidgetOpen';

const HelpWidget: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  // Persist the open state so it survives navigation while the user is touring.
  useEffect(() => {
    const wasOpen = sessionStorage.getItem(STORAGE_KEY);
    if (wasOpen === '1') setOpen(true);
  }, []);

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, open ? '1' : '0');
  }, [open]);

  // Hide on public pages where users aren't authenticated yet.
  const hiddenRoutes = ['/', '/login', '/register', '/forgot-password', '/reset-password', '/pricing'];
  if (hiddenRoutes.includes(location.pathname)) return null;

  const current = STEPS[step];
  const isFirst = step === 0;
  const isLast = step === STEPS.length - 1;

  const handleGo = () => {
    if (current.cta) {
      navigate(current.cta.route);
    }
  };

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="How to use ViewPoint"
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-3 bg-coral-600 hover:bg-coral-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all group"
        >
          <HelpCircle className="w-5 h-5" />
          <span className="text-sm font-medium hidden sm:inline">How to use ViewPoint</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed bottom-6 right-6 z-40 w-[calc(100vw-3rem)] sm:w-96 max-w-sm bg-white dark:bg-background-secondary rounded-2xl shadow-2xl border border-stone-200 dark:border-coral-500/30 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-coral-500/10 to-transparent border-b border-stone-200 dark:border-coral-500/20">
            <div className="flex items-center gap-2 text-coral-700 dark:text-foreground-muted">
              {current.icon}
              <span className="text-sm font-semibold">How to use ViewPoint</span>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="p-1 rounded-lg hover:bg-coral-500/10 text-stone-500 dark:text-foreground-muted"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <div className="p-5">
            <h3 className="text-base font-bold text-stone-900 dark:text-white mb-2">
              {current.title}
            </h3>
            <p className="text-sm text-stone-600 dark:text-foreground-secondary leading-relaxed">
              {current.body}
            </p>

            {current.cta && (
              <button
                onClick={handleGo}
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-coral-600 dark:text-foreground-muted hover:text-coral-700 dark:hover:text-foreground-secondary"
              >
                {current.cta.label}
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Progress dots */}
          <div className="flex items-center justify-center gap-1.5 px-4 pb-2">
            {STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                aria-label={`Go to step ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? 'w-6 bg-coral-500' : 'w-1.5 bg-stone-300 dark:bg-coral-500/30 hover:bg-coral-400'
                }`}
              />
            ))}
          </div>

          {/* Footer nav */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-stone-200 dark:border-coral-500/20">
            <button
              onClick={() => setStep(s => Math.max(0, s - 1))}
              disabled={isFirst}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-stone-600 dark:text-foreground-muted hover:bg-stone-100 dark:hover:bg-coral-500/10 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back
            </button>
            <span className="text-xs text-stone-400 dark:text-foreground-muted tabular-nums">
              {step + 1} / {STEPS.length}
            </span>
            {isLast ? (
              <button
                onClick={() => setOpen(false)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-coral-600 hover:bg-coral-700 text-white rounded-lg transition-colors"
              >
                Done
              </button>
            ) : (
              <button
                onClick={() => setStep(s => Math.min(STEPS.length - 1, s + 1))}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-coral-600 hover:bg-coral-700 text-white rounded-lg transition-colors"
              >
                Next
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default HelpWidget;
